import { useCallback, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/hooks/use-auth';
import { postJson } from '@/lib/post-json';

/**
 * Full-page sign-in with the operator's main-app credentials (super-admin
 * only). The ops server proxies the backend's real login+MFA endpoints and
 * keeps nothing but a session id — prod logins add an email OTP step.
 */
export function LoginScreen() {
  const { signedIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const otpStep = mfaToken !== '';

  const submitCredentials = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = (await postJson('/api/auth/start', { email, password })) as
        | { mfaRequired: true; mfaToken: string; maskedEmail: string }
        | { mfaRequired: false; email: string };
      setPassword('');
      if (result.mfaRequired) {
        setMfaToken(result.mfaToken);
        setMaskedEmail(result.maskedEmail);
      } else {
        signedIn(result.email);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [email, password, signedIn]);

  const submitCode = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = (await postJson('/api/auth/verify', { mfaToken, code })) as {
        email: string;
      };
      signedIn(result.email);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [mfaToken, code, signedIn]);

  return (
    <div className="bg-background flex h-screen items-center justify-center">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Overhead Ops</CardTitle>
          <p className="text-muted-foreground text-sm">
            {otpStep
              ? `Enter the 6-digit code sent to ${maskedEmail || 'your email'}.`
              : 'Sign in with your Overhead account (super-admin only).'}
          </p>
        </CardHeader>
        <CardContent>
          {!otpStep ? (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                void submitCredentials();
              }}
            >
              <Input
                type="email"
                autoComplete="email"
                placeholder="you@getoverhead.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
              <Input
                type="password"
                autoComplete="current-password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {error && <p className="text-destructive text-sm">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading || !email || !password}>
                {loading ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          ) : (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                void submitCode();
              }}
            >
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                autoFocus
              />
              {error && <p className="text-destructive text-sm">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading || code.length !== 6}>
                {loading ? 'Verifying…' : 'Verify'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setMfaToken('');
                  setCode('');
                  setError(null);
                }}
              >
                Back
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
